import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { getTableShapes } from 'restaurantAdmin/floorPlans/apiHelpers';
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue';
import {
  getHumanReadableIconNamesFromTableShapes,
  getIconNamesFromTableShapes,
  TableShape,
} from '@shared/types/floorPlans';

const SEARCH_DEBOUNCE_MS = 250;
const TABLE_SHAPES_QUERY_KEY = 'tableShapes';

export const EXCLUDED_FLOOR_PLAN_TABLE_ICONS: string[] = [
  'Round44D',
  'Square22E',
  'Rectangle42I',
  'NoTableNotUserA',
  'NoTableNotUserB',
  'TableCombination',
];

export interface UseTableShapesReturn {
  tableShapes: TableShape[];
  iconNames: string[];
  humanReadableIconNames: Record<string, string>;
  isLoading: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
}

export function useTableShapes(
  excludeIconNames?: string[],
): UseTableShapesReturn {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    SEARCH_DEBOUNCE_MS,
  );

  const queryParams = useMemo(
    () => ({
      ...(debouncedSearchQuery.trim() && {
        search: debouncedSearchQuery.trim(),
      }),
      excludeIconNames: excludeIconNames ?? EXCLUDED_FLOOR_PLAN_TABLE_ICONS,
    }),
    [debouncedSearchQuery],
  );

  const { data: tableShapes = [], isPending: isLoading } = useQuery({
    queryKey: [TABLE_SHAPES_QUERY_KEY, queryParams],
    queryFn: () => getTableShapes(queryParams),
  });

  const iconNames = useMemo(
    () => getIconNamesFromTableShapes(tableShapes),
    [tableShapes],
  );
  const humanReadableIconNames = useMemo(
    () => getHumanReadableIconNamesFromTableShapes(tableShapes),
    [tableShapes],
  );

  const stableSetSearchQuery = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  return {
    tableShapes,
    iconNames,
    humanReadableIconNames,
    isLoading,
    searchQuery,
    setSearchQuery: stableSetSearchQuery,
  };
}
